/**
 * v1Endpoints — R3-S3 (F3.5)
 *
 * Katalog der stabilen Public API (v1), 1:1 uebernommen aus dem Monolithen
 * (app/views/home/api.haml, Kategorien und Reihenfolge unveraendert).
 * Die v1-Routen bleiben laut Runde-3-Entscheidung unveraendert bestehen
 * (config/routes.rb, namespace :api) — es gibt bewusst KEINEN v2-Klon.
 *
 * Die Labels sind i18n-Keys im Namespace "apidocs" (publicApi.endpoints.*),
 * die Kategorienamen unter publicApi.categories.*.
 */

// heliumtracker-web#9: die Basis-URL der Public API MUSS aus der zentralen
// v2-Konfiguration kommen (VITE_API_BASE_URL, wie ueberall sonst im FE, z.B.
// api-client.ts). VITE_PUBLIC_API_BASE_URL bleibt als expliziter Override
// erhalten, faellt aber nicht mehr auf die alte v1-Domain
// (www.heliumtracker.io) zurueck, sondern auf VITE_API_BASE_URL selbst.
export const PUBLIC_API_BASE_URL =
  (import.meta.env.VITE_PUBLIC_API_BASE_URL as string | undefined) ??
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  'https://api.heliumtracker.io'

export type V1Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

export type V1Endpoint = {
  /** i18n-Key unterhalb von publicApi.endpoints */
  labelKey: string
  method: V1Method
  /** Zusaetzliche Methode fuer PATCH/PUT-Doppelrouten in v1 */
  methodAlias?: string
  path: string
}

export type V1Category = {
  /** i18n-Key unterhalb von publicApi.categories */
  labelKey: string
  id: string
  endpoints: V1Endpoint[]
}

export const V1_ENDPOINT_CATALOG: V1Category[] = [
  {
    id: 'single-hotspot',
    labelKey: 'singleHotspot',
    endpoints: [
      { labelKey: 'hotspotDetail', method: 'GET', path: '/api/hotspots/HOTSPOT_ID' },
      { labelKey: 'hotspotRewardSummary', method: 'GET', path: '/api/hotspots/HOTSPOT_ID/rewards_total' },
      { labelKey: 'hotspotFullRewards', method: 'GET', path: '/api/hotspots/HOTSPOT_ID/rewards_full' },
      { labelKey: 'hotspotMonthlyRewards', method: 'GET', path: '/api/hotspots/HOTSPOT_ID/rewards' },
      { labelKey: 'hotspotActivities', method: 'GET', path: '/api/hotspots/HOTSPOT_ID/activities' },
    ],
  },
  {
    id: 'all-hotspots-dashboard',
    labelKey: 'dashboard',
    endpoints: [
      { labelKey: 'accountRewardSummary', method: 'GET', path: '/api/dashboard' },
      { labelKey: 'accountFullRewards', method: 'GET', path: '/api/dashboard/rewards_full' },
      { labelKey: 'accountActivities', method: 'GET', path: '/api/dashboard/activities' },
      { labelKey: 'accountMonthlyIncome', method: 'GET', path: '/api/dashboard/daily_income' },
      { labelKey: 'dashboardFilters', method: 'GET', path: '/api/dashboard/filters' },
    ],
  },
  {
    id: 'user-hotspots',
    labelKey: 'userHotspots',
    endpoints: [
      { labelKey: 'listUserHotspots', method: 'GET', path: '/api/user_hotspots' },
      { labelKey: 'createUserHotspot', method: 'POST', path: '/api/user_hotspots' },
      { labelKey: 'showUserHotspot', method: 'GET', path: '/api/user_hotspots/USERHOTSPOT_ID' },
      { labelKey: 'updateUserHotspot', method: 'PATCH', methodAlias: 'PUT', path: '/api/user_hotspots/USERHOTSPOT_ID' },
      { labelKey: 'deleteUserHotspot', method: 'DELETE', path: '/api/user_hotspots/USERHOTSPOT_ID' },
    ],
  },
  {
    id: 'contacts',
    labelKey: 'contacts',
    endpoints: [
      { labelKey: 'listContacts', method: 'GET', path: '/api/contacts' },
      { labelKey: 'createContact', method: 'POST', path: '/api/contacts' },
      { labelKey: 'showContact', method: 'GET', path: '/api/contacts/CONTACT_ID' },
      { labelKey: 'updateContact', method: 'PATCH', methodAlias: 'PUT', path: '/api/contacts/CONTACT_ID' },
      { labelKey: 'deleteContact', method: 'DELETE', path: '/api/contacts/CONTACT_ID' },
    ],
  },
  {
    id: 'commissions',
    labelKey: 'commissions',
    endpoints: [
      { labelKey: 'listCommissions', method: 'GET', path: '/api/shares' },
      { labelKey: 'createCommission', method: 'POST', path: '/api/shares' },
      { labelKey: 'showCommission', method: 'GET', path: '/api/shares/COMISSION_ID' },
      { labelKey: 'updateCommission', method: 'PATCH', methodAlias: 'PUT', path: '/api/shares/COMISSION_ID' },
      { labelKey: 'deleteCommission', method: 'DELETE', path: '/api/shares/COMISSION_ID' },
    ],
  },
  {
    id: 'user-wallets',
    labelKey: 'userWallets',
    endpoints: [
      { labelKey: 'listUserWallets', method: 'GET', path: '/api/user_wallets' },
      { labelKey: 'createUserWallet', method: 'POST', path: '/api/user_wallets' },
      { labelKey: 'showUserWallet', method: 'GET', path: '/api/user_wallets/USER_WALLET_ID' },
      { labelKey: 'updateUserWallet', method: 'PATCH', methodAlias: 'PUT', path: '/api/user_wallets/USER_WALLET_ID' },
      { labelKey: 'deleteUserWallet', method: 'DELETE', path: '/api/user_wallets/USER_WALLET_ID' },
    ],
  },
  {
    id: 'general',
    labelKey: 'general',
    endpoints: [
      { labelKey: 'news', method: 'GET', path: '/api/news' },
      { labelKey: 'timezones', method: 'GET', path: '/api/timezones' },
      { labelKey: 'currencies', method: 'GET', path: '/api/currencies' },
    ],
  },
]

export const V1_ENDPOINT_COUNT = V1_ENDPOINT_CATALOG.reduce(
  (sum, category) => sum + category.endpoints.length,
  0
)

export function buildV1Curl(endpoint: V1Endpoint): string {
  return `curl -X ${endpoint.method} --header "Api-Key: YOUR_API_KEY" ${PUBLIC_API_BASE_URL}${endpoint.path}`
}

/** curl-Beispiel fuer die Public API mit Api-Key-Header (v1-Paritaet). Verwendet von ApiKeyCard.tsx. */
export function buildPublicApiCurl(apiKey: string, path = '/api/dashboard'): string {
  return `curl --header "Api-Key: ${apiKey}" ${PUBLIC_API_BASE_URL}${path}`
}
